import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useProfile } from '@/hooks/useProfile';
import type { Tables } from '@/integrations/supabase/types';

type DealershipRow = Tables<'dealerships'>;

export type ImpersonationMode = 'none' | 'view_as_rep' | 'view_as_dealership_admin';

interface AdminDealershipContextValue {
  // Active dealership (either user's own or admin-selected)
  activeDealershipId: string | null;
  activeDealership: DealershipRow | null;
  
  // All dealerships (for global admin)
  allDealerships: DealershipRow[];
  
  // Impersonation mode for UI preview
  impersonationMode: ImpersonationMode;
  
  // For global admins only
  setActiveDealershipId: (id: string | null) => void;
  setImpersonationMode: (mode: ImpersonationMode) => void;
  
  // Pinned and recent dealerships
  pinnedDealershipIds: string[];
  recentDealershipIds: string[];
  pinDealership: (id: string) => void;
  unpinDealership: (id: string) => void;
  
  // State flags
  isGlobalAdmin: boolean;
  isImpersonating: boolean;
  loading: boolean;
  
  // Refresh dealership data
  refreshActiveDealership: () => Promise<void>;
  refreshAllDealerships: () => Promise<void>;
}

const STORAGE_KEY_ACTIVE = 'global_admin_active_dealership';
const STORAGE_KEY_PINNED = 'global_admin_pinned_dealerships';
const STORAGE_KEY_RECENT = 'global_admin_recent_dealerships';
const STORAGE_KEY_IMPERSONATION = 'global_admin_impersonation_mode';
const MAX_RECENT = 5;

const AdminDealershipContext = createContext<AdminDealershipContextValue | null>(null);

export const useAdminDealershipContext = () => {
  const context = useContext(AdminDealershipContext);
  if (!context) {
    throw new Error('useAdminDealershipContext must be used within an AdminDealershipProvider');
  }
  return context;
};

// Safe hook that returns null if not in provider
export const useAdminDealershipContextSafe = () => {
  return useContext(AdminDealershipContext);
};

interface AdminDealershipProviderProps {
  children: React.ReactNode;
}

export const AdminDealershipProvider: React.FC<AdminDealershipProviderProps> = ({ children }) => {
  const supabase = useSupabase();
  const { userId } = useAuth();
  const { isGlobalAdmin, loading: rolesLoading } = useUserRoles();
  const { profile, isLoading: profileLoading } = useProfile();
  
  // State
  const [allDealerships, setAllDealerships] = useState<DealershipRow[]>([]);
  const [activeDealership, setActiveDealership] = useState<DealershipRow | null>(null);
  const [adminSelectedDealershipId, setAdminSelectedDealershipId] = useState<string | null>(null);
  const [impersonationMode, setImpersonationModeState] = useState<ImpersonationMode>('none');
  const [pinnedDealershipIds, setPinnedDealershipIds] = useState<string[]>([]);
  const [recentDealershipIds, setRecentDealershipIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Compute the active dealership ID
  const activeDealershipId = useMemo(() => {
    if (isGlobalAdmin && adminSelectedDealershipId) {
      return adminSelectedDealershipId;
    }
    return profile?.dealership_id || null;
  }, [isGlobalAdmin, adminSelectedDealershipId, profile?.dealership_id]);
  
  // Load persisted state from localStorage (global admin only)
  useEffect(() => {
    if (!isGlobalAdmin) return;
    
    try {
      const savedActive = localStorage.getItem(STORAGE_KEY_ACTIVE);
      const savedPinned = localStorage.getItem(STORAGE_KEY_PINNED);
      const savedRecent = localStorage.getItem(STORAGE_KEY_RECENT);
      const savedImpersonation = localStorage.getItem(STORAGE_KEY_IMPERSONATION);
      
      if (savedActive) setAdminSelectedDealershipId(savedActive);
      if (savedPinned) setPinnedDealershipIds(JSON.parse(savedPinned));
      if (savedRecent) setRecentDealershipIds(JSON.parse(savedRecent));
      if (savedImpersonation) setImpersonationModeState(savedImpersonation as ImpersonationMode);
    } catch (e) {
      console.error('[AdminDealershipContext] Error loading from localStorage:', e);
    }
  }, [isGlobalAdmin]);
  
  // Fetch all dealerships (global admin only)
  const refreshAllDealerships = useCallback(async () => {
    if (!isGlobalAdmin) {
      setAllDealerships([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('dealerships')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setAllDealerships((data || []) as unknown as DealershipRow[]);
    } catch (e) {
      console.error('[AdminDealershipContext] Error fetching dealerships:', e);
    }
  }, [isGlobalAdmin, supabase]);
  
  // Fetch active dealership details
  const refreshActiveDealership = useCallback(async () => {
    if (!activeDealershipId) {
      setActiveDealership(null);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('dealerships')
        .select('*')
        .eq('id', activeDealershipId)
        .single();
      
      if (error) throw error;
      setActiveDealership(data as unknown as DealershipRow);
    } catch (e) {
      console.error('[AdminDealershipContext] Error fetching active dealership:', e);
      setActiveDealership(null);
    }
  }, [activeDealershipId, supabase]);
  
  // Initialize data
  useEffect(() => {
    if (rolesLoading || profileLoading) return;
    
    const init = async () => {
      setLoading(true);
      
      if (isGlobalAdmin) {
        await refreshAllDealerships();
      }
      
      await refreshActiveDealership();
      setLoading(false);
    };
    
    // Fire-and-forget: init awaits its refreshers and sets loading=false in all paths.
    void init();
  }, [isGlobalAdmin, rolesLoading, profileLoading, refreshAllDealerships, refreshActiveDealership]);
  
  // Set active dealership (global admin only)
  const setActiveDealershipId = useCallback((id: string | null) => {
    if (!isGlobalAdmin) return;
    
    setAdminSelectedDealershipId(id);
    
    // Persist to localStorage
    if (id) {
      localStorage.setItem(STORAGE_KEY_ACTIVE, id);
      
      // Add to recent (avoiding duplicates, max 5)
      setRecentDealershipIds(prev => {
        const filtered = prev.filter(r => r !== id);
        const updated = [id, ...filtered].slice(0, MAX_RECENT);
        localStorage.setItem(STORAGE_KEY_RECENT, JSON.stringify(updated));
        return updated;
      });
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE);
    }
  }, [isGlobalAdmin]);
  
  // Set impersonation mode
  const setImpersonationMode = useCallback((mode: ImpersonationMode) => {
    setImpersonationModeState(mode);
    localStorage.setItem(STORAGE_KEY_IMPERSONATION, mode);
  }, []);
  
  // Pin/unpin dealership
  const pinDealership = useCallback((id: string) => {
    setPinnedDealershipIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      localStorage.setItem(STORAGE_KEY_PINNED, JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  const unpinDealership = useCallback((id: string) => {
    setPinnedDealershipIds(prev => {
      const updated = prev.filter(p => p !== id);
      localStorage.setItem(STORAGE_KEY_PINNED, JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  // Refresh active dealership when selection changes
  useEffect(() => {
    if (activeDealershipId) {
      // Fire-and-forget: refreshActiveDealership handles its own errors.
      void refreshActiveDealership();
    }
  }, [activeDealershipId, refreshActiveDealership]);
  
  const isImpersonating = impersonationMode !== 'none';
  
  const value: AdminDealershipContextValue = {
    activeDealershipId,
    activeDealership,
    allDealerships,
    impersonationMode,
    setActiveDealershipId,
    setImpersonationMode,
    pinnedDealershipIds,
    recentDealershipIds,
    pinDealership,
    unpinDealership,
    isGlobalAdmin,
    isImpersonating,
    loading: loading || rolesLoading || profileLoading,
    refreshActiveDealership,
    refreshAllDealerships,
  };
  
  return (
    <AdminDealershipContext.Provider value={value}>
      {children}
    </AdminDealershipContext.Provider>
  );
};

export default AdminDealershipProvider;
